import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth-context";
import { CountryProvider } from "./lib/country-context";
import { OperatorProvider } from "./lib/operator-context";
import { Toaster } from "./components/ui/sonner";

// Main Pages
import LandingPage from "./pages/LandingPage";
import ExplorePage from "./pages/ExplorePage";
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import RidePage from "./pages/RidePage";
import RentalPage from "./pages/RentalPage";
import DeliveryPage from "./pages/DeliveryPage";
import WalletPage from "./pages/WalletPage";
import ProfilePage from "./pages/ProfilePage";
import DriverDashboard from "./pages/DriverDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PaymentCancelPage from "./pages/PaymentCancelPage";

// Operator Pages (Central de Monitoreo)
import OperatorLogin from "./pages/OperatorLogin";
import OperatorConsole from "./pages/OperatorConsole";

// Public Pages
import TripFollowPage from "./pages/TripFollowPage";

// Services Pages
import RideHailingPage from "./pages/services/RideHailingPage";
import VehicleRentalsPage from "./pages/services/VehicleRentalsPage";
import LogisticsPage from "./pages/services/LogisticsPage";
import DigiWalletPage from "./pages/services/DigiWalletPage";

// Company Pages
import AboutPage from "./pages/company/AboutPage";
import DriversPage from "./pages/company/DriversPage";
import VehicleOwnersPage from "./pages/company/VehicleOwnersPage";
import SupportPage from "./pages/company/SupportPage";

// Legal Pages
import TermsPage from "./pages/legal/TermsPage";
import PrivacyPage from "./pages/legal/PrivacyPage";
import CookiesPage from "./pages/legal/CookiesPage";

import "./App.css";

// Protected Route wrapper
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#00BFA6] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/home" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/explore" element={<ExplorePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/payment/cancel" element={<PaymentCancelPage />} />
      
      {/* Operator Routes (Central de Monitoreo) */}
      <Route path="/operator-login" element={<OperatorLogin />} />
      <Route path="/operator-console" element={<OperatorProvider><OperatorConsole /></OperatorProvider>} />
      
      {/* Public Trip Following (no auth required) */}
      <Route path="/trip-follow/:token" element={<TripFollowPage />} />
      
      {/* Services Pages (Public) */}
      <Route path="/services/ride-hailing" element={<RideHailingPage />} />
      <Route path="/services/vehicle-rentals" element={<VehicleRentalsPage />} />
      <Route path="/services/logistics" element={<LogisticsPage />} />
      <Route path="/services/digi-wallet" element={<DigiWalletPage />} />
      
      {/* Company Pages (Public) */}
      <Route path="/company/about" element={<AboutPage />} />
      <Route path="/company/drivers" element={<DriversPage />} />
      <Route path="/company/vehicle-owners" element={<VehicleOwnersPage />} />
      <Route path="/company/support" element={<SupportPage />} />
      
      {/* Legal Pages (Public) */}
      <Route path="/legal/terms" element={<TermsPage />} />
      <Route path="/legal/privacy" element={<PrivacyPage />} />
      <Route path="/legal/cookies" element={<CookiesPage />} />
      
      {/* Protected routes */}
      <Route path="/home" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
      <Route path="/ride" element={<ProtectedRoute><RidePage /></ProtectedRoute>} />
      <Route path="/rental" element={<ProtectedRoute><RentalPage /></ProtectedRoute>} />
      <Route path="/delivery" element={<ProtectedRoute><DeliveryPage /></ProtectedRoute>} />
      <Route path="/wallet" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      
      {/* Role-specific routes */}
      <Route 
        path="/driver" 
        element={
          <ProtectedRoute allowedRoles={['driver', 'admin']}>
            <DriverDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <CountryProvider>
        <BrowserRouter>
          <div className="min-h-screen bg-[#F5F7FA]">
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </div>
        </BrowserRouter>
      </CountryProvider>
    </AuthProvider>
  );
}

export default App;
